import { Features } from '@experiences/feature-flags';
import { UiSuspensefulOutlet } from '@experiences/ui-common';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { routePaths } from '../../../common/constants/routePaths';
import { useOrganizationName } from '../../../common/hooks/useOrganizationName';
import BreadcrumbProvider from '../../../common/providers/BreadcrumbProvider';
import { useTenantIdContext } from '../../../common/providers/TenantIdProvider';
import type { UiRouteObject } from '../../../container/routeConfigs/UiRouteObject';
import { AiTrustLayerTabSettingsProvider } from '../AiTrustRoutes';
import { useModelHubTitleKey } from './useModelHubTitleKey';

const TenantView = React.lazy(() =>
    import('./TenantView').then(module => ({ default: module.TenantView }))
);

const ReplaceModelPage = React.lazy(() =>
    import('./ReplaceModelPage').then(module => ({ default: module.ReplaceModelPage }))
);

const CreatePolicyPage = React.lazy(() =>
    import('./dataResidencyPolicy/CreatePolicyPage').then(module => ({ default: module.CreatePolicyPage }))
);

const TenantModelHubProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { formatMessage: translate } = useIntl();

    const organizationName = useOrganizationName();
    const { tenantName } = useTenantIdContext();
    const modelHubTitleKey = useModelHubTitleKey();

    const breadCrumbLinks = useMemo(() => [
        {
            index: 0,
            link: routePaths.admin,
            name: organizationName,
        },
        {
            index: 1,
            link: routePaths.adminTenantHome,
            name: tenantName ?? translate({ id: 'CLIENT_TENANT' }),
        },
        {
            index: 2,
            link: routePaths.adminTenantModelHub,
            name: translate({ id: modelHubTitleKey }),
        },
    ], [ modelHubTitleKey, organizationName, tenantName, translate ]);

    return <BreadcrumbProvider breadcrumbs={breadCrumbLinks}>
        {children}
    </BreadcrumbProvider>;
};

export const TenantModelHubRoutes: UiRouteObject = {
    path: routePaths.adminTenant,
    element: <TenantModelHubProvider>
        <UiSuspensefulOutlet />
    </TenantModelHubProvider>,
    children: [
        {
            path: routePaths.adminTenantModelHub,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerModelHub.name}>
                    <TenantView />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
        {
            path: routePaths.adminTenantModelHubAddCustomModel,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerModelHub.name}>
                    <ReplaceModelPage />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
        {
            path: routePaths.adminTenantModelHubCreatePolicy,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerModelHub.name}>
                    <CreatePolicyPage />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
        {
            path: routePaths.adminTenantModelHubEditPolicy,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerModelHub.name}>
                    <CreatePolicyPage />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
    ],
};
